import dayjs from "dayjs";

/**
 * Days of access granted past a subscription's expiration (its next billing
 * cycle) before the restaurant is cut off. Mirrors the backend's
 * `RestaurantSubscriptionTierService.GRACE_PERIOD_DAYS`.
 */
export const SUBSCRIPTION_GRACE_PERIOD_DAYS = 3;

export enum SubscriptionStatus {
  /** Not expired, and bound to a billing date. */
  Active = "active",
  /** Expired but still inside the grace period — access is not cut yet. */
  Grace = "grace",
  /** Past the grace period: the gates no longer grant access. */
  Expired = "expired",
  /** No expiration date on record, so it never expires. */
  Perpetual = "perpetual",
}

/** The shape both the ops list and the restaurateur card can produce. */
export type SubscriptionStatusInput = {
  nextBillingCycle: string | null;
  expired: boolean;
  active: boolean;
};

export const getSubscriptionStatus = ({
  nextBillingCycle,
  expired,
  active,
}: SubscriptionStatusInput): SubscriptionStatus => {
  if (!active) return SubscriptionStatus.Expired;
  if (expired) return SubscriptionStatus.Grace;
  if (nextBillingCycle === null) return SubscriptionStatus.Perpetual;
  return SubscriptionStatus.Active;
};

/** The last day access is still granted: expiration + the grace period. */
export const getAccessEndsOn = (nextBillingCycle: string) =>
  dayjs(nextBillingCycle).add(SUBSCRIPTION_GRACE_PERIOD_DAYS, "day");

/**
 * Whole days from today until `date`, in the viewer's timezone. Negative once
 * the date has passed, `0` on the day itself.
 */
export const daysUntil = (date: dayjs.Dayjs | string) =>
  dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");

/**
 * Days of access left on an expired subscription before it is revoked — the
 * remainder of the grace period. Never negative.
 */
export const getGraceDaysLeft = (nextBillingCycle: string) =>
  Math.max(daysUntil(getAccessEndsOn(nextBillingCycle)), 0);
